import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import useLocales from '../../hooks/useLocales';
import { RootState, useSelector } from '../../redux/store';
import getOrganizationDisplayName from '../../utils/getOrganizationDisplayName';
import type { OrganizationType } from '../../types/organization';

interface SelectOrganizationsProps {
  id?: string;
  name?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  isClearable?: boolean;
  displayTradingCompany?: boolean;
  options?: OrganizationType[];
  selected: OrganizationType[];
  optionsFilter?: (options) => OrganizationType[];
  onChange?: (organizations: OrganizationType[]) => void;
}

/**
 * 組織を複数選択するコンポーネント。OrganizationType[]を引数に取るコールバックがあれば実行する。
 * @param id? [string]
 * @param name? [string]
 * @param className? [string]
 * @param placeholder? [string]
 * @param disabled? [boolean]
 * @param isClearable? [boolean]
 * @param displayTradingCompany? [boolean]
 * @param options [OrganizationType[]]
 * @param selected [OrganizationType[] | null]
 * @param optionsFilter?: (options: OrganizationType[]) => OrganizationType[];
 * @param onChange?: (organizations: OrganizationType[]) => void;
 * @returns React.ReactElement
 */
const SelectOrganizations = ({
  id = 'selectOrganizations',
  name = 'selectOrganizations',
  className = '',
  disabled = false,
  isClearable = false,
  displayTradingCompany = true,
  options,
  selected,
  optionsFilter = (options) => options,
  onChange = () => {},
}: SelectOrganizationsProps): React.ReactElement => {
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { organizations } = useSelector((state: RootState) => state.organization);
  const { organizationBase } = useSelector((state: RootState) => state.options);
  const { tradingCompany } = useSelector((state: RootState) => state.options);
  const [selectOptions, setSelectOptions] = useState<OrganizationType[]>(options ?? organizations ?? []);
  const [selectedOption, setSelectedOption] = useState<OrganizationType[]>(selected);
  const getOptionLabel = (option: OrganizationType) =>
    getOrganizationDisplayName(option, { organizationBases: organizationBase, tradingCompanies: displayTradingCompany ? tradingCompany : undefined, language: currentLang.value });
  const getOptionValue = (option: OrganizationType) => String(option.id);

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);
  useEffect(() => {
    setSelectedOption(selected);
  }, [selected]);

  // プルダウン選択肢の設定
  // ・options引数があればそれを利用する
  // ・optionsがなかったら、organizationsがあればそれを利用する
  // ・両方なければ空配列にする
  useEffect(() => {
    if (options) {
      const filteredOrganizations: OrganizationType[] = optionsFilter(options);
      setSelectOptions(filteredOrganizations);
    } else if (organizations) {
      const filteredOrganizations: OrganizationType[] = optionsFilter(organizations);
      setSelectOptions(filteredOrganizations);
    } else {
      setSelectOptions([]);
    }
  }, [options, organizations]);

  // EVENT HANDLER
  // ================================================================================

  // プルダウンから組織を選んだとき
  const handleSelectChange = (orgs): void => {
    setSelectedOption(orgs);
    onChange(orgs);
  };

  // VIEW
  // ================================================================================

  return (
    <Select
      inputId={id}
      name={name}
      isMulti={true}
      options={selectOptions}
      value={selectedOption}
      onChange={handleSelectChange}
      isDisabled={disabled}
      isClearable={isClearable}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder={t('dropdown.organization')}
      className={'react-select-container' + ' ' + className}
      classNamePrefix="react-select"
    />
  );
};

export default SelectOrganizations;
